import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, MessageCircle } from 'lucide-react';
import { formatPrice, formatMileage, formatPowerShort } from '../utils/formatters';
import { getTransmissionLabel, getConditionBadges, getFuelTypeLabel, getBodyTypeLabel } from '../constants/vehicleFields';

const MarketplaceCard = ({ listing, isOwn }) => {
  const vehicle = listing.vehicle || {};
  const dealer = listing.dealer || {};

  const whatsappUrl = dealer.phone
    ? `https://wa.me/${dealer.phone.replace(/[^0-9+]/g, '')}?text=${encodeURIComponent(
        `Bonjour, je suis intéressé par votre ${vehicle.make} ${vehicle.model} ${vehicle.year || ''} publiée sur la Marketplace Carmin.`
      )}`
    : null;

  // Check if listing is recent (< 48h)
  const isNew = listing.created_at && (Date.now() - new Date(listing.created_at).getTime()) < 48 * 60 * 60 * 1000;

  return (
    <Link to={`/marketplace/${listing.id}`} className="block">
      <div className={`bg-dark-card rounded-xl hover:border-gray-600 transition-colors overflow-hidden ${isOwn ? 'border-2 border-accent-purple/50' : 'border border-dark-border'}`}>
        {/* Image */}
        <div className="aspect-[4/3] bg-dark-hover relative">
          {vehicle.images && vehicle.images.length > 0 ? (
            <img
              src={vehicle.images[0]}
              alt={`${vehicle.make} ${vehicle.model}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted">
              Pas d'image
            </div>
          )}
          {/* Own badge */}
          {isOwn && (
            <span className="absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium bg-accent-purple text-white">
              Mon annonce
            </span>
          )}
          {/* Photo count */}
          {vehicle.images && vehicle.images.length > 1 && (
            <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
              {vehicle.images.length} photos
            </span>
          )}
        </div>

        {/* Content */}
        <div className="p-3.5">
          {/* NEU badge + Title */}
          <div className="flex items-center gap-2">
            {isNew && (
              <span className="px-1.5 py-0.5 bg-carmin text-white text-[10px] font-bold uppercase rounded">
                Nouveau
              </span>
            )}
            <h3 className="font-bold text-primary truncate">
              {vehicle.make} {vehicle.model}
            </h3>
          </div>
          {vehicle.version && (
            <p className="text-xs text-muted truncate mt-0.5">{vehicle.version}</p>
          )}

          {/* Price */}
          <div className="mt-1.5">
            <span className="text-lg font-bold text-carmin">
              {listing.pro_price ? formatPrice(listing.pro_price) : 'Prix sur demande'}
            </span>
            {listing.pro_price && <span className="text-xs text-muted ml-1.5">TVAC</span>}
          </div>

          {/* Specs row 1 - main specs */}
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {vehicle.year && (
              <span className="px-2 py-0.5 bg-dark-hover rounded text-xs text-label">{vehicle.year}</span>
            )}
            {vehicle.mileage != null && (
              <span className="px-2 py-0.5 bg-dark-hover rounded text-xs text-label">{formatMileage(vehicle.mileage)}</span>
            )}
            {vehicle.fuel_type && (
              <span className="px-2 py-0.5 bg-dark-hover rounded text-xs text-label">{getFuelTypeLabel(vehicle.fuel_type)}</span>
            )}
            {vehicle.transmission && (
              <span className="px-2 py-0.5 bg-dark-hover rounded text-xs text-label">
                {getTransmissionLabel(vehicle.transmission)}
              </span>
            )}
          </div>

          {/* Specs row 2 - power, body, color */}
          {(vehicle.power_kw || vehicle.power_hp || vehicle.body_type || vehicle.color) && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {(vehicle.power_hp || vehicle.power_kw) && (
                <span className="px-2 py-0.5 bg-dark-hover rounded text-xs text-label">
                  {formatPowerShort(vehicle.power_kw, vehicle.power_hp)}
                </span>
              )}
              {vehicle.body_type && (
                <span className="px-2 py-0.5 bg-dark-hover rounded text-xs text-label">{getBodyTypeLabel(vehicle.body_type)}</span>
              )}
              {vehicle.color && (
                <span className="px-2 py-0.5 bg-dark-hover rounded text-xs text-label">{vehicle.color}</span>
              )}
            </div>
          )}

          {/* Condition badges */}
          {(() => {
            const badges = getConditionBadges(vehicle);
            if (badges.length === 0) return null;
            return (
              <div className="mt-2 flex flex-wrap gap-1">
                {badges.map(badge => {
                  const Icon = badge.icon;
                  return (
                    <span key={badge.label} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-400">
                      <Icon className="h-2.5 w-2.5" /> {badge.label}
                    </span>
                  );
                })}
              </div>
            );
          })()}

          {/* Dealer info + Contact */}
          <div className="mt-3 pt-2.5 border-t border-dark-border flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              {dealer.logo_url ? (
                <img src={dealer.logo_url} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
              ) : (
                <Building2 className="h-3.5 w-3.5 text-muted flex-shrink-0" />
              )}
              <span className="text-xs text-muted truncate">{dealer.display_name}</span>
            </div>
            {!isOwn && whatsappUrl && (
              <span
                onClick={(e) => {
                  e.preventDefault();
                  window.open(whatsappUrl, '_blank');
                }}
                className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-400 rounded text-xs font-medium hover:bg-green-500/20 transition-colors flex-shrink-0"
              >
                <MessageCircle className="h-3 w-3" />
                Contacter
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default MarketplaceCard;
